import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDeleteQuery } from "../hooks";
import Panel from "./Panel";
import Grid from "./Grid";
import Modal from "./Modal";
import EditButton from "./EditButton";
import SubmissionAdd from "./SubmissionAdd";
import ConfirmationModal from "./ConfirmationModal";
import Actions from "./Actions";
import AddButton from "./AddButton";
import DeleteButton from "./DeleteButton";
import { useGetQuery } from "../hooks";
import { useDataContext } from "../contexts";
import { PAGE_SIZE } from "../constants";

export default function SubmissionCRUD({ user }) {
  const navigate = useNavigate();
  const { getMemberUserById } = useDataContext();

  const [page, setPage] = useState(1);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteModalId, setDeleteModalId] = useState(null);

  const submissionsQuery = useGetQuery("submissions", {
    enabled: !!user,
    queryParams: {
      page: page,
      user: user.id,
      page_size: PAGE_SIZE,
    },
  });

  const deleteSubmission = useDeleteQuery("submissions");

  if (!user) {
    return (
      <Panel left="Submissions">
        <div className="p-4 text-center text-gray-500">
          No user data available
        </div>
      </Panel>
    );
  }

  if (submissionsQuery.isLoading) {
    return null;
  }

  return (
    <Panel left="Submissions">
      <Grid
        columnsTemplate="justify-between grid-cols-[auto_auto_auto]"
        data={submissionsQuery.data.results}
        renderTestId={(submission) => {
          const userName = getMemberUserById(submission.user)?.name;
          return `Submission ${userName} ${submission.startDateFormatted}`;
        }}
        columns={[
          {
            header: "User",
            render: (submission) => {
              const userId = submission.user;
              const user = getMemberUserById(userId);
              if (!user) {
                return "Unknown";
              }
              return user.name;
            },
          },
          { header: "Date", key: "startDateFormatted" },
          {
            header: "Actions",
            render: (submission) => (
              <Actions>
                <EditButton
                  key="view"
                  onClick={() => navigate(`/submission/${submission.id}`)}
                />
                <DeleteButton
                  key="delete"
                  onClick={() => setDeleteModalId(submission.id)}
                />
              </Actions>
            ),
          },
        ]}
        pagination
        page={page}
        totalPages={Math.ceil(submissionsQuery.data.count / PAGE_SIZE)}
        onPageChange={setPage}
      />
      <AddButton
        className="mt-4"
        onClick={() => {
          setPage(1);
          setAddModalOpen(true);
        }}
      />
      <Modal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        heading="Add Submission"
      >
        <SubmissionAdd
          callback={(record) => {
            navigate(`/submission/${record.id}`);
          }}
        />
      </Modal>
      <ConfirmationModal
        isOpen={deleteModalId !== null}
        onClose={() => setDeleteModalId(null)}
        heading="Delete Submission"
        message="Are you sure you want to delete this submission?"
        confirmLabel="Delete"
        onConfirm={() => {
          deleteSubmission.mutate(deleteModalId);
          setDeleteModalId(null);
        }}
      />
    </Panel>
  );
}
